import { useState } from "react";
import { Download, Share2, Loader2, FileArchive, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errorMessage, isPaywallError } from "@/lib/api";
import { canShareFiles, downloadFile } from "@/lib/share";

const ZIP_NAME = "clicou-guardou-meus-dados.zip";

export function ExportDataDialog({ open, onClose, onPaywall }) {
  const [state, setState] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [file, setFile] = useState(null);

  const close = () => { setState("idle"); setProgress(0); setFile(null); onClose(); };

  const generate = async () => {
    setState("loading"); setProgress(0);
    try {
      const res = await api.get("/users/me/export", { responseType: "blob", onDownloadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); } });
      setFile(new File([res.data], ZIP_NAME, { type: "application/zip" }));
      setState("ready");
    } catch (e) {
      setState("idle");
      if (isPaywallError(e)) { close(); onPaywall?.(); return; }
      toast.error(errorMessage(e));
    }
  };
  const share = async () => {
    try { await navigator.share({ files: [file], title: "Meus dados — Clicou, Guardou!" }); } catch (e) { if (e?.name !== "AbortError") toast.error("Não foi possível compartilhar o ZIP."); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-sm overflow-x-hidden rounded-3xl p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]" data-testid="export-dialog">
        <DialogTitle className="flex items-center gap-2"><FileArchive size={20} style={{ color: "#0A6373" }} /> Exportar meus dados</DialogTitle>
        <DialogDescription className="break-words text-sm leading-relaxed">
          Geramos um arquivo ZIP com tudo o que é seu: <b>dados.json</b> (memórias, lembretes, categorias e metadados), <b>memorias.csv</b> e a pasta <b>anexos/</b> com fotos e documentos. Itens da Pasta Segura não são incluídos.
        </DialogDescription>
        {state === "idle" && <button onClick={generate} className="ga-btn-primary w-full" data-testid="export-generate-button"><FileArchive size={18} /> Gerar arquivo ZIP</button>}
        {state === "loading" && (
          <div className="space-y-2" data-testid="export-progress">
            <div className="flex items-center gap-2 text-sm"><Loader2 size={16} className="animate-spin" /> Preparando seu arquivo{progress ? ` — ${progress}%` : "..."}</div>
            <div className="ga-surface h-2 overflow-hidden rounded-full"><div className="h-full rounded-full transition-all" style={{ width: `${progress || 15}%`, background: "#13C9A3" }} /></div>
          </div>
        )}
        {state === "ready" && file && (
          <div className="space-y-2" data-testid="export-ready">
            <p className="flex items-center gap-1.5 text-sm" style={{ color: "#0A6373" }}><CheckCircle2 size={16} /> Pronto! {(file.size / 1024 / 1024).toFixed(2)} MB</p>
            <button onClick={() => downloadFile(file, ZIP_NAME)} className="ga-btn-primary w-full" data-testid="export-download-button"><Download size={18} /> Baixar ZIP</button>
            {canShareFiles([file]) && <button onClick={share} className="ga-btn-ghost w-full" data-testid="export-share-button"><Share2 size={18} /> Compartilhar ZIP</button>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
